/**
 * Strip capability tokens and PII out of URLs before they reach PostHog.
 *
 * Security audit finding 17 (2026-08-12). `posthog.capture('$pageview')`
 * attaches `$current_url` — the full href — and several of this app's routes
 * carry a *self-authenticating* token: `routers/vault.py` states outright that
 * the token is the only authentication. So a PostHog reader (or anyone holding
 * a project API key) could replay a flight selection or a vault write as the
 * customer. The 1 h / 24 h TTLs and single-use semantics bound it; they do not
 * close it.
 *
 * Tokens ride in two shapes and each needs its own rule:
 *
 * - **Query** — `/vault/enter-secret?token=`, `/integrations/enter-secret?token=`,
 *   and landing's `?t=` fallback.
 * - **Path**: `/flights/:token`, `/stays/:token`, `/trips/:token`, `/cc/:token`,
 *   `/s/:linkId/i/:token`, and landing's `/a/<token>`.
 *
 * ## Query: allowlist, not denylist
 *
 * Query values are redacted unless the parameter is on {@link ANALYTICS_PARAMS}.
 * A denylist of secret-sounding names is what the equivalent backend filter
 * (`app/utils/url_secret_span_filter.py`) was written to replace — OTel's own
 * `redact_url` is exactly such a denylist, and it is what let `token` through
 * in the first place.
 *
 * The allowlist direction is what makes this worth more than the finding asked
 * for: the booking flow already puts `guest_email`, `guest_first_name` and
 * `guest_last_name` in the query string, and OAuth puts `state`/`code` there.
 * None of those are tokens, so a token-shaped denylist would have shipped them
 * to PostHog forever. Fail-closed catches them, and catches the next one.
 *
 * **Keys are kept, values replaced.** `?token=<redacted>&utm_source=blog` still
 * says which parameters a request carried, which is most of the debugging
 * value, while no future parameter can leak by not being on a list.
 *
 * ## Path: an explicit route table, kept honest by a test
 *
 * Path tokens are matched by {@link TOKENISED_PATH_PATTERNS} rather than by
 * guessing which segments "look opaque". Entropy heuristics were considered and
 * rejected: they would redact blog slugs like `top-10-tips` on the marketing
 * site while still missing a short token, i.e. wrong in both directions.
 *
 * An explicit table can go stale, so it is not trusted to stay complete —
 * `urlSanitizer.routes.test.ts` parses the route table out of `App.tsx` and
 * fails when a route declares a token-shaped parameter this file does not
 * cover. The guarantee that "a new page cannot forget" is enforced in CI, not
 * assumed at runtime.
 */

export const REDACTED = '<redacted>'

/**
 * Query parameters whose *values* survive into analytics.
 *
 * Everything here is either ad-network attribution, our own campaign tagging,
 * or PostHog's own cross-browser session-stitching parameters (see
 * `appendPostHogStitchParams`) — none of it authenticates anything. Adding a
 * parameter here is a deliberate decision to publish its values; when in doubt
 * leave it off, since the key is reported either way.
 */
const ANALYTICS_PARAMS: ReadonlySet<string> = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'msclkid',
  'ttclid',
  'twclid',
  'li_fat_id',
  // Our own landing-variant tagging (`/?v=3`, `/?variant=v2`).
  'v',
  'variant',
  // Referral code. Public by construction — it is printed in share copy and
  // the whole point is that it travels in links.
  'ref',
  // PostHog's own stitching parameters.
  'ph_session_id',
  'ph_distinct_id',
])

/**
 * Path shapes carrying a capability token, with the token as capture group 1.
 *
 * Anchored, and matched against `pathname` only. Ordered longest-prefix-first
 * so `/s/:linkId/i/:token` is tried before anything that could shadow it.
 */
const TOKENISED_PATH_PATTERNS: readonly RegExp[] = [
  // Scheduling invitee link. `linkId` is deliberately preserved: it identifies
  // the booking page (which is meant to be shareable) while `token` is the part
  // that authenticates one invitee.
  /^(?:\/s\/[^/]+\/i)\/([^/]+)/,
  /^(?:\/flights)\/([^/]+)/,
  /^(?:\/stays)\/([^/]+)/,
  /^(?:\/trips)\/([^/]+)/,
  /^(?:\/cc)\/([^/]+)/,
  // Landing's public assistant-persona page (`/a/<token>`, nginx-rewritten to
  // `/a/index.html`). Lives on the marketing origin, not this app, but the
  // sanitizer is shared in spirit and an app-side link to it must not leak.
  /^(?:\/a)\/([^/]+)/,
]

function redactPath(pathname: string): string {
  for (const pattern of TOKENISED_PATH_PATTERNS) {
    const match = pattern.exec(pathname)
    if (!match) continue
    const token = match[1]
    // A zero-length or already-redacted segment has nothing to hide.
    if (!token || token === REDACTED) return pathname
    const start = match.index + match[0].length - token.length
    return pathname.slice(0, start) + REDACTED + pathname.slice(start + token.length)
  }
  return pathname
}

function redactQuery(params: URLSearchParams): void {
  for (const key of Array.from(new Set(params.keys()))) {
    if (ANALYTICS_PARAMS.has(key)) continue
    const occurrences = params.getAll(key).length
    params.delete(key)
    for (let i = 0; i < occurrences; i += 1) params.append(key, REDACTED)
  }
}

/**
 * Redact a URL for analytics. Returns the input unchanged when it cannot be
 * parsed — a value we do not understand is not a value we should rewrite into
 * something misleading, and it is not a token we can find either.
 *
 * The hash fragment is dropped wholesale: `buildPostHogHashFragment` puts
 * session IDs there, deep links put arbitrary state there, and no analytics
 * here reads it.
 */
export function sanitizeAnalyticsUrl(rawUrl: string): string {
  let url: URL
  try {
    url = new URL(rawUrl)
  } catch {
    return rawUrl
  }

  url.pathname = redactPath(url.pathname)
  redactQuery(url.searchParams)
  url.hash = ''
  return url.toString()
}

/** Redact a bare pathname (PostHog's `$pathname`, which carries no origin). */
export function sanitizeAnalyticsPathname(rawPath: string): string {
  const [pathname, ...rest] = rawPath.split('?')
  const redactedPath = redactPath(pathname ?? rawPath)
  if (rest.length === 0) return redactedPath
  const params = new URLSearchParams(rest.join('?'))
  redactQuery(params)
  const query = params.toString()
  return query ? `${redactedPath}?${query}` : redactedPath
}

/**
 * Property keys are matched by SHAPE, not by a fixed list.
 *
 * A fixed list was the first version and it was wrong: posthog-js derives a
 * whole second family of person properties in `sessionPropsManager`, which maps
 * every set-once prop to `$session_entry_<key>` — and, note, renames
 * `$current_url` to `url` on the way, so the key is `$session_entry_url`, not
 * the `$session_entry_current_url` you would guess. Those ride `$set_once`,
 * i.e. the durable person-property path. Enumerating them invites exactly the
 * miss this comment describes, and posthog is free to add more.
 *
 * Matching by shape is the same fail-closed direction as the query allowlist:
 * a key that *looks* like it holds a URL gets sanitized, and a new one is
 * covered the day posthog ships it. Over-matching is harmless — a value that
 * does not parse as a URL is returned untouched (`$referring_domain` is a bare
 * domain, so it no-ops), and a non-string value is skipped.
 *
 * `$referrer` matters as much as `$current_url`: same-origin navigation sends
 * the full previous URL, so moving off `/vault/enter-secret?token=…` hands
 * PostHog the token as the referrer of the *next* pageview.
 */
const PATH_KEY_PATTERN = /pathname/i
const URL_KEY_PATTERN = /url|referr/i

/**
 * Sanitize one property bag in place.
 *
 * Applied to every captured event rather than to `$pageview` alone, because
 * PostHog attaches `$current_url` to *all* of them — an ordinary
 * `trackEvent('cta_clicked')` fired from a token page carries the token too.
 */
export function sanitizeAnalyticsProperties(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  for (const [key, value] of Object.entries(properties)) {
    if (typeof value !== 'string') continue
    // Pathname first: `$session_entry_pathname` matches both patterns, and
    // parsing an origin-less path as a URL would throw and no-op.
    if (PATH_KEY_PATTERN.test(key)) properties[key] = sanitizeAnalyticsPathname(value)
    else if (URL_KEY_PATTERN.test(key)) properties[key] = sanitizeAnalyticsUrl(value)
  }
  return properties
}

/**
 * Does this URL carry a capability token?
 *
 * Used to switch session replay off, which `before_send` cannot reach: the
 * rrweb meta event carries the full href inside `$snapshot_data`, so a recorded
 * `/flights/<token>` session hands the token to a PostHog reader however
 * clean the event properties are.
 *
 * Deliberately narrower than "does the sanitizer change anything": the query
 * allowlist redacts *unknown* parameters too, so `/login?error=x` would differ
 * and would silently disable replay across most of the app. This asks only
 * about capability tokens.
 */
export function hasCapabilityToken(rawUrl: string): boolean {
  let pathname: string
  let params: URLSearchParams
  try {
    const url = new URL(rawUrl, 'https://placeholder.invalid')
    pathname = url.pathname
    params = url.searchParams
  } catch {
    return false
  }
  if (TOKENISED_PATH_PATTERNS.some((pattern) => pattern.test(pathname))) return true
  // The two `?token=` entry pages, plus landing's `?t=` fallback.
  return params.has('token') || params.has('t')
}

const URL_OR_PATH_IN_TEXT = /https?:\/\/[^\s'"<>`]+|\/[^\s'"<>`]+/g

/**
 * Redact every URL or path found inside free text, such as an error message.
 *
 * Exception events carry their message inside `$exception_list`, which the
 * key-shaped property pass never reaches, and an error built from a URL or a
 * backend detail can quote a capability token verbatim.
 */
export function sanitizeAnalyticsText(text: string): string {
  return text.replace(URL_OR_PATH_IN_TEXT, (match) =>
    match.startsWith('/') ? sanitizeAnalyticsPathname(match) : sanitizeAnalyticsUrl(match),
  )
}

function sanitizeExceptionProperties(properties: Record<string, unknown>): void {
  const message = properties['$exception_message']
  if (typeof message === 'string') properties['$exception_message'] = sanitizeAnalyticsText(message)
  const list = properties['$exception_list']
  if (!Array.isArray(list)) return
  for (const entry of list) {
    if (entry && typeof entry === 'object' && typeof (entry as { value?: unknown }).value === 'string') {
      const item = entry as { value: string }
      item.value = sanitizeAnalyticsText(item.value)
    }
  }
}

/** Minimal shape of posthog-js's `CaptureResult`, to avoid importing its types here. */
interface CaptureLike {
  properties?: Record<string, unknown>
  $set?: Record<string, unknown>
  $set_once?: Record<string, unknown>
}

/**
 * `before_send` hook for `posthog.init`.
 *
 * `before_send` rather than `sanitize_properties`: the latter is deprecated as
 * of posthog-js 1.402 and logs a warning on every init.
 *
 * **`$set` and `$set_once` are sanitized too, and that is not belt-and-braces.**
 * PostHog persists `$initial_current_url` / `$initial_referrer` as PERSON
 * properties via `$set_once`, so an event-properties-only hook would strip the
 * token from the event and then write it to the person record — where it is
 * durable and survives the event's own retention. A person property is the
 * worse of the two places for a capability token to land.
 *
 * Returns the event; returning `null` would drop it entirely, which is not the
 * job here. A null input (PostHog can pass one through a chain of hooks) is
 * passed straight back.
 */
export function sanitizeBeforeSend<T extends CaptureLike | null>(event: T): T {
  if (!event) return event
  for (const bag of [event.properties, event.$set, event.$set_once]) {
    if (bag) sanitizeAnalyticsProperties(bag)
  }
  if (event.properties) sanitizeExceptionProperties(event.properties)
  return event
}
